import {
  BookMarked,
  BriefcaseBusiness,
  Gem,
  HelpCircle,
  Home,
  Inbox,
  LayoutList,
  type LucideIcon,
  Megaphone,
  PanelLeftClose,
  PanelLeftOpen,
  Radar,
  Settings,
  Users,
} from "lucide-react";
import * as React from "react";

import { useClerk, useUser } from "@clerk/clerk-react";
import posthog from "posthog-js";
import { Link, useLocation, useParams } from "react-router";
import { useActiveOrg } from "@/hooks/use-active-org";
import { useOrgQuota } from "@/hooks/use-org-quota";
import { useWorkspaceStore } from "@/store/workspace-store";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { NavMain } from "./nav-main";
import { WorkspaceSwitcher } from "./workspace-switcher";

interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
}

const data: { navMain: NavItem[] } = {
  navMain: [
    {
      title: "Home",
      url: "/dashboard",
      icon: Home,
    },
    {
      title: "Inbox",
      url: "/inbox",
      icon: Inbox,
    },
    {
      title: "Pipeline",
      url: "/deals",
      icon: LayoutList,
    },
    {
      title: "Portfolio",
      url: "/portco",
      icon: BriefcaseBusiness,
    },
    {
      title: "Team",
      url: "/team",
      icon: Users,
    },
    {
      title: "Deal Radar",
      url: "/deal-radar",
      icon: Radar,
    },
    {
      title: "Raiseboard",
      url: "/raise-board",
      icon: Megaphone,
    },
    {
      title: "Lookbooks",
      url: "/lookbooks",
      icon: BookMarked,
    },
  ],
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { isMobile, setOpenMobile, toggleSidebar, state } = useSidebar();

  const handleLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const { workspaceId: urlWorkspaceId } = useParams<{ workspaceId: string }>();
  const { activeWorkspace } = useWorkspaceStore();
  const { isOrgAdmin, billingHref: billingLink } = useActiveOrg();
  const { quota } = useOrgQuota();
  const location = useLocation();
  const { openUserProfile } = useClerk();
  const { user } = useUser();
  const userLabel =
    user?.fullName ||
    user?.primaryEmailAddress?.emailAddress ||
    "Account";
  const userInitial = (userLabel[0] || "A").toUpperCase();

  const currentWorkspaceId = urlWorkspaceId || activeWorkspace?._id;

  // Every item here is reachable by any workspace member. Lookbooks used to be
  // filtered out for members because listing them 403'd; it no longer does, and
  // the page itself renders read-only for anyone who isn't the owner.
  const transformedNavMain = data.navMain.map((item) => {
    const workspaceUrl = currentWorkspaceId
      ? `/${currentWorkspaceId}${item.url}`
      : item.url;
    return {
      ...item,
      isActive:
        location.pathname === workspaceUrl ||
        location.pathname.startsWith(`${workspaceUrl}/`),
    };
  });

  const isSettingsActive = location.pathname.includes("/settings");

  return (
    <Sidebar collapsible="icon" {...props}>
      {/* Header: WorkspaceSwitcher + collapse toggle when expanded;
          a single centered expand button when collapsed (avoids avatar
          clipping in the 3rem-wide icon mode and provides the only
          affordance to re-open the sidebar). */}
      <SidebarHeader
        className={
          state === "collapsed"
            ? "flex flex-col items-center gap-1 border-b border-sidebar-border px-2 py-2"
            : "h-14 flex flex-row items-center gap-1 border-b border-sidebar-border px-2"
        }
      >
        {state === "collapsed" ? (
          <>
            <WorkspaceSwitcher />
            <button
              onClick={toggleSidebar}
              className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              aria-label="Expand sidebar"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </button>
          </>
        ) : (
          <>
            <div className="flex-1 min-w-0">
              <WorkspaceSwitcher />
            </div>
            <button
              onClick={toggleSidebar}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              aria-label="Collapse sidebar"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </>
        )}
      </SidebarHeader>

      {/* Nav */}
      <SidebarContent>
        <NavMain
          items={transformedNavMain}
          onLinkClick={handleLinkClick}
          dataTourMap={{
            "Getting Started": "getting-started",
            Home: "dashboard",
            Inbox: "inbox",
            Pipeline: "deals",
            Portfolio: "portco",
            Team: "team",
            "Deal Radar": "deal-radar",
          }}
        />
      </SidebarContent>

      {/* Footer: Premium Deals tracker + Settings + Support */}
      <SidebarFooter className="border-t border-sidebar-border px-2 py-3 space-y-px">
        <SidebarMenu>
          {quota && (() => {
            const usageText = `Premium Deals: ${quota.premium_deals_used} / ${quota.effective_premium_deals_cap ?? quota.premium_deals_cap}`;
            const menuItem = (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  // SidebarMenuButton's built-in tooltip only fires in collapsed
                  // mode; an admin sees inline numbers in expanded mode, so the
                  // short text only matters when the sidebar is iconified.
                  tooltip={usageText}
                  className={cn(
                    "text-muted-foreground",
                    !billingLink && "cursor-default hover:bg-transparent active:bg-transparent"
                  )}
                >
                  {billingLink ? (
                    <Link to={billingLink} onClick={handleLinkClick}>
                      <Gem className="size-4" />
                      <span className="flex-1">Premium Deals</span>
                      <span className="font-mono text-xs tabular-nums text-foreground/80">
                        {quota.premium_deals_used}
                        <span className="text-muted-foreground">
                          {" "}/ {quota.effective_premium_deals_cap ?? quota.premium_deals_cap}
                        </span>
                      </span>
                    </Link>
                  ) : (
                    <span>
                      <Gem className="size-4" />
                      <span className="flex-1">Premium Deals</span>
                      <span className="font-mono text-xs tabular-nums text-foreground/80">
                        {quota.premium_deals_used}
                        <span className="text-muted-foreground">
                          {" "}/ {quota.effective_premium_deals_cap ?? quota.premium_deals_cap}
                        </span>
                      </span>
                    </span>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            );

            // Non-admins can't act on the row — tooltip explains the read-only state.
            if (!isOrgAdmin) {
              return (
                <Tooltip>
                  <TooltipTrigger asChild>{menuItem}</TooltipTrigger>
                  <TooltipContent side="right" align="center" className="max-w-xs">
                    <div className="space-y-1">
                      <div className="font-medium">{usageText}</div>
                      <div className="text-[11px] text-muted-foreground">
                        View only — contact a workspace admin to change the plan.
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            }

            return menuItem;
          })()}
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={isSettingsActive}
              data-tour="workspace-settings"
              className={cn(
                !isSettingsActive && "text-muted-foreground"
              )}
            >
              <Link
                to={`/${currentWorkspaceId}/settings`}
                onClick={handleLinkClick}
              >
                <Settings className="size-4" />
                <span>Settings</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              data-tour="support"
              className="text-muted-foreground"
            >
              <a
                href="https://support.dgpt.io/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => posthog.capture("suport_btn_clicked")}
              >
                <HelpCircle className="size-4" />
                <span>Support</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Account — whole row opens the Clerk user-profile popover. */}
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip={userLabel}
              className="text-muted-foreground"
              onClick={() => openUserProfile()}
            >
              {user?.imageUrl ? (
                <img
                  src={user.imageUrl}
                  alt={userLabel}
                  className="size-4 rounded-full object-cover"
                />
              ) : (
                <span className="flex size-4 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-foreground">
                  {userInitial}
                </span>
              )}
              <span className="truncate">{userLabel}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
